import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  Button,
  EmptyState,
  EmptyStateProps,
  OneLineList,
  Pagination,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
  Tabs,
  TabItem,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import SendSupplementModal from "./SendSupplementModal";
import {
  useSupplementPlan,
  useSupplementPlanWrites,
  useSupplementPlans,
} from "../hooks/useSupplementPlans";
import { formatSupplementDate } from "../supplementMapping";
import { saveSupplementPlanPdf } from "../supplementDocument";
import {
  DEFAULT_PAGE_SIZE,
  fetchSupplementPlanPdf,
} from "../services/supplementPlansService";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// A deep import rather than the clients barrel: that barrel pulls in the
// client pages, and the client module imports this panel back for its Plans
// tab.
import ChooseClientModal from "../../clients/components/ChooseClientModal";
import { ROUTES, createSupplementPath } from "../../../routes/paths";
import { ApiError } from "../../../lib/http";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import type {
  SupplementPlanState,
  SupplementPlanSummary,
} from "../types/supplements.types";

/**
 * Which plans the table lists: every one, or only those still being written.
 * The same two tabs the meal plan tables have, asked of the server the same
 * way — see `useSupplementPlans`.
 */
type SupplementPlanFilter = "all" | "draft";

const FILTER_TABS: TabItem<SupplementPlanFilter>[] = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
];

const headCell =
  "px-3 sm:px-4 py-3 text-[13px] font-normal text-ink-muted bg-white shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] text-ink";

/**
 * What a plan's status is called on screen, and the pill it is shown in.
 *
 * The backend says `ACTIVE` for a plan the client has; the app has always
 * called that `sent`, and the design's column says Active. The same two words
 * and the same two pills the meal plan tables use — the three Plans tabs are
 * read one after the other, so a status cannot mean one thing on one of them.
 */
const STATUS_LABELS: Record<SupplementPlanState, string> = {
  draft: "Draft",
  sent: "Active",
};

const STATUS_STYLES: Record<SupplementPlanState, string> = {
  draft: "bg-[#F1F4F6] text-ink-muted",
  sent: "bg-brand-soft text-[#00899B]",
};

export interface ClientSupplementsPanelProps {
  /**
   * The client's `public_id` — whose supplement plans are listed. Left off,
   * the panel lists the whole workspace's, which is what the Create plan
   * screen shows: every row then names its client, and creating one asks who
   * it is for first.
   */
  clientId?: string;
  clientName?: string;
  /**
   * Whether this workspace may still write, read once by the screen above —
   * see `ClientDetailPage`. The list itself stays readable either way, and so
   * does a plan opened from it.
   */
  gate: SubscriptionGate;
}

/**
 * Supplement plans — a client's, or everyone's: one row per plan, with what
 * is on it.
 *
 * The rows come from `GET /api/v1/supplement-plans/`, a page at a time,
 * filtered by `client` where the panel was given one, and the Draft tab and
 * the search box are the server's too — this endpoint takes a `status` and a
 * `search`, so both narrow the whole list rather than the page in hand.
 *
 * Opening one is a screen rather than a dialog: a supplement carries a
 * schedule and a row per dose, and a plan carries several supplements, which
 * is more than a dialog over a table can hold. See `CreateSupplementPage`.
 *
 * A stored plan can be sent from the row menu and downloaded from it as the
 * document the backend renders. What it cannot be is deleted — there is no
 * endpoint for that — so the menu offers three things and not four.
 */
const ClientSupplementsPanel: React.FC<ClientSupplementsPanelProps> = ({
  clientId,
  clientName,
  gate,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  const [filter, setFilter] = useState<SupplementPlanFilter>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  /** The plan the send dialog is open over, if any. */
  const [sending, setSending] = useState<SupplementPlanSummary | null>(null);
  /** And the one whose document is being fetched, so its row can say so. */
  const [downloading, setDownloading] = useState<string | null>(null);
  /**
   * Whether the who-is-it-for question is up. Only ever on the workspace-wide
   * list: a client's own panel already knows, and goes straight to the screen.
   */
  const [choosing, setChoosing] = useState(false);

  const createGate = gate.props("create a supplement plan");
  const sendGate = gate.props("send a supplement plan");

  const {
    plans: rows,
    count,
    status: loadStatus,
    error,
    refused,
  } = useSupplementPlans({
    client: clientId,
    status: filter === "draft" ? "draft" : "all",
    search,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Only `send` is used here, and a send names the plan rather than the
  // client — so the workspace-wide list, which has no client, loses nothing.
  const { send } = useSupplementPlanWrites(clientId ?? "");

  // The plan the dialog is over, read whole — the dialog names what is about
  // to go, and the list row carries only the names.
  const { plan: opened } = useSupplementPlan(sending?.id ?? "");

  /**
   * Where the create screen comes back to when it was opened from here.
   *
   * Router state rather than anything in the URL: the screen's own default is
   * the client's Plans tab, which is right when that is where it was opened
   * from and wrong from the Create plan screen. The path names a screen and a
   * list, nothing about a person — see `CreateSupplementPage`.
   */
  const returnState = clientId
    ? undefined
    : { returnTo: `${ROUTES.createPlan}?kind=supplement` };

  /** The create screen, on the plan that was pressed — opened to be read. */
  const open = (plan: SupplementPlanSummary) =>
    navigate(createSupplementPath(plan.clientId || clientId || "", plan.id), {
      state: returnState,
    });

  /** The same screen on a blank card, for the client it is being written for. */
  const start = (forClient: string) =>
    navigate(createSupplementPath(forClient), { state: returnState });

  /**
   * The way in from the Create button: the screen, the question of who it is
   * for, or the reason neither will open. Reading is never gated.
   */
  const createFromGate = () => {
    if (createGate.disabled) return createGate.onDisabledClick?.();
    if (clientId) return start(clientId);
    setChoosing(true);
  };

  // The server's count for this client and this search, not the rows on
  // screen: this table only ever holds one page of them.
  const totalPages = Math.max(1, Math.ceil(count / DEFAULT_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * DEFAULT_PAGE_SIZE;

  // A list that shrank past the page in hand lands on the last one, rather
  // than leaving the table asking for a page that is no longer there.
  useEffect(() => {
    if (loadStatus === "ready" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  // A page that arrives quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  /** What a refused request tells the user, in the words the API used. */
  const reportFailure = (what: string, reason: unknown) =>
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );

  const handleSend = async (
    plan: SupplementPlanSummary,
    emails: string[]
  ) => {
    try {
      const message = await send(plan.id, emails);
      setSending(null);
      // The backend's own wording where it gave one — a change there reaches
      // the dietitian without this line being touched.
      toast.success(
        "Supplements sent",
        message ?? `${plan.shortName} is on its way to ${plan.clientName}.`
      );
    } catch (reason) {
      reportFailure("Could not send the supplements", reason);
    }
  };

  /**
   * Fetches the document and hands it to the browser.
   *
   * The bytes rather than a link: the endpoint wants the same token every
   * other call does, so a plain `<a href>` to it would be an unauthenticated
   * request. See `saveSupplementPlanPdf`, which is what happens to them.
   */
  const handleDownload = async (plan: SupplementPlanSummary) => {
    if (downloading) return;

    setDownloading(plan.id);
    try {
      saveSupplementPlanPdf(plan, await fetchSupplementPlanPdf(getToken, plan.id));
    } catch (reason) {
      reportFailure("Could not download the supplement plan", reason);
    } finally {
      setDownloading(null);
    }
  };

  /**
   * A failure comes before emptiness: an empty table under "no supplement
   * plans" would read as an answer, when nothing was ever heard back.
   *
   * A refusal is the exception — 403 is what a workspace with no plan running
   * gets, and a practice that has never been able to write a supplement plan
   * has never written one. That is drawn as the empty list it is.
   */
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed" && !refused) {
      const state = failedEmptyState(
        "We could not load this client’s supplement plans"
      );
      return error ? { ...state, description: error } : state;
    }

    if (search.trim()) return searchEmptyState("supplement plans");

    // A draft is something the screen was left in the middle of, so an empty
    // Draft tab is good news rather than a gap to be filled — it gets no
    // button, only the word that nothing is sitting half-finished.
    if (filter === "draft")
      return {
        title: clientName
          ? "No draft supplement plans for this client"
          : "No draft supplement plans",
        description:
          "Plans you start but do not send wait here until you finish them.",
      };

    return {
      title: "No supplement plans yet",
      description: clientName
        ? `Anything you put ${clientName.split(" ")[0]} on will be listed here with its dose, its unit and the days it is taken.`
        : "Anything you put a client on will be listed here with its dose, its unit and the days it is taken.",
      action: (
        <Button
          size="sm"
          startIcon={<Plus className="h-4 w-4" />}
          onClick={createFromGate}
          {...createGate}
        >
          Create supplement
        </Button>
      ),
    };
  };

  return (
    <section className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 sm:px-6 pt-5">
        <h2 className="text-[16px] 3xl:text-[18px] font-medium text-ink">
          Supplement
        </h2>
        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                // A narrower list starts at its own beginning. Page three of
                // the unfiltered list is rarely page three of the filtered one.
                setPage(1);
              }}
              placeholder="Search"
              aria-label="Search supplement plans"
              className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
            />
          </div>
          <Button
            size="sm"
            startIcon={<Plus className="h-4 w-4" />}
            onClick={createFromGate}
            {...createGate}
          >
            Create
          </Button>
        </div>
      </div>

      {/* Under the header rather than beside it, the way the client's meal
          plan panel has them — the search box and the Create button keep the
          right of the header row, and the two tabs get a row of their own. */}
      <div className="flex-shrink-0 px-5 sm:px-6 mt-4 border-b border-line">
        <Tabs
          tabs={FILTER_TABS}
          activeTab={filter}
          onChange={(next) => {
            setFilter(next);
            // A narrower list starts at its own beginning, the same way a
            // search does.
            setPage(1);
          }}
        />
      </div>

      {phase !== "ready" && rows.length === 0 ? (
        // One block the size of the table, rather than a page of blocks in the
        // shape of its cells — see `TABLE_BODY_SKELETON`.
        <div className="flex-1 min-h-0 p-4 sm:p-6">
          <span role="status" className="sr-only">
            Loading supplement plans…
          </span>
          <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
          {/* Full height only when the rows are gone, so the empty state sits
              in the middle of the card rather than under a header. */}
          <table
            className={`w-full min-w-[820px] table-fixed border-collapse text-left ${
              rows.length === 0 ? "h-full" : ""
            }`}
          >
            {/* Sl at 5%, matching the meal and exercise tables beside it — the
                three Plans tabs are read one after the other, so a number
                column that changed width between them would read as the table
                shifting rather than as the columns differing.

                Plan name takes the most of it, the way the exercise table's
                does: the supplement names are one or two words each and go
                behind a +n count once they stop fitting, where a plan's name
                truncates into nothing that tells the rows apart. */}
            {/* Listing everyone, a Client name column goes in after the
                plan's, and the plan name gives up the width for it — on that
                list the client is what tells the rows apart. */}
            {clientId ? (
              <colgroup>
                <col className="w-[5%]" />
                <col className="w-[33%]" />
                <col className="w-[27%]" />
                <col className="w-[14%]" />
                <col className="w-[16%]" />
                <col className="w-[5%]" />
              </colgroup>
            ) : (
              <colgroup>
                <col className="w-[5%]" />
                <col className="w-[24%]" />
                <col className="w-[18%]" />
                <col className="w-[22%]" />
                <col className="w-[12%]" />
                <col className="w-[14%]" />
                <col className="w-[5%]" />
              </colgroup>
            )}
            <thead className="sticky top-0 z-10">
              <tr>
                <th scope="col" className={headCell}>
                  Sl
                </th>
                <th scope="col" className={headCell}>
                  Plan name
                </th>
                {!clientId && (
                  <th scope="col" className={headCell}>
                    Client name
                  </th>
                )}
                <th scope="col" className={headCell}>
                  Supplements
                </th>
                <th scope="col" className={headCell}>
                  Status
                </th>
                <th scope="col" className={headCell}>
                  Created on
                </th>
                <th scope="col" className={headCell}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={clientId ? 6 : 7} className="align-middle">
                    <EmptyState {...emptyState()} />
                  </td>
                </tr>
              ) : (
                rows.map((plan: SupplementPlanSummary, index: number) => (
                  <tr
                    key={plan.id}
                    onClick={() => open(plan)}
                    className="cursor-pointer border-b border-line transition-colors last:border-0 hover:bg-[#F8FEFF]"
                  >
                    <td className={bodyCell}>
                      {String(startIndex + index + 1).padStart(2, "0")}
                    </td>
                    <td className={`${bodyCell} truncate font-medium`}>
                      {/* The whole row is clickable for pointers, but the name
                          is a real button so the list stays reachable by
                          keyboard — the same arrangement the exercise table
                          uses. The whole of the backend's name, truncated
                          where the column runs out, with the `title` to read
                          it by. */}
                      <button
                        type="button"
                        title={plan.name}
                        onClick={(event) => {
                          event.stopPropagation();
                          open(plan);
                        }}
                        className="max-w-full truncate rounded text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                      >
                        {plan.name}
                      </button>
                    </td>
                    {!clientId && (
                      <td className={`${bodyCell} truncate`}>
                        {plan.clientName}
                      </td>
                    )}
                    {/* `min-w-0` on the cell, because `OneLineList` measures
                        itself against whatever contains it — given its
                        content's width it would never overflow, and so would
                        never count anything behind the pill. */}
                    <td className={`${bodyCell} min-w-0`}>
                      {/* The names the list row carries — `supplement_names` —
                          rather than how many there are. `OneLineList` draws a
                          dash for a plan with none. */}
                      <OneLineList items={plan.names} label="supplements" />
                    </td>
                    <td className={bodyCell}>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-medium ${
                          STATUS_STYLES[plan.status]
                        }`}
                      >
                        {STATUS_LABELS[plan.status]}
                      </span>
                    </td>
                    <td className={bodyCell}>
                      {formatSupplementDate(plan.createdOn)}
                    </td>
                    {/* The menu stops its own clicks, so opening it does not
                        also open the plan the row belongs to. */}
                    <td
                      className={bodyCell}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <RowActionsMenu
                        label={`Actions for ${plan.shortName}`}
                        actions={[
                          {
                            label: "View supplement",
                            onSelect: () => open(plan),
                          },
                          {
                            // What the row says depends on whether this plan
                            // has been to the client before, the same way an
                            // exercise plan's menu used to read.
                            label:
                              plan.status === "sent"
                                ? "Send again"
                                : "Send supplement",
                            disabled: sendGate.disabled,
                            onSelect: sendGate.disabled
                              ? sendGate.onDisabledClick ?? (() => {})
                              : () => setSending(plan),
                          },
                          {
                            label:
                              downloading === plan.id
                                ? "Preparing PDF…"
                                : "Download PDF",
                            // Reading is never gated, and a document is a
                            // read. Only one at a time, because the row has
                            // no way to say which of two it is preparing.
                            disabled: downloading !== null,
                            onSelect: () => void handleDownload(plan),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        resultCount={rows.length}
      />

      {choosing && (
        <ChooseClientModal
          title="Create supplement"
          label="Choose client for supplement"
          onClose={() => setChoosing(false)}
          onChoose={(client) => start(client.id)}
        />
      )}

      {sending && (
        <SendSupplementModal
          clientName={sending.clientName || clientName || ""}
          // The row names the client and nothing more, so the dialog looks the
          // address up itself — see `SendSupplementModalProps.clientId`.
          clientId={sending.clientId || clientId}
          // The plan read whole, so the dialog names what is on it rather than
          // counting it. Empty until the detail lands, which is a dialog that
          // opens on its addresses and fills in behind them.
          supplements={opened?.supplements ?? []}
          // The row itself, so the dialog's Download is the row menu's
          // Download — the same document, asked for from wherever the plan is
          // in front of you.
          plan={sending}
          onClose={() => setSending(null)}
          onSend={(emails) => handleSend(sending, emails)}
        />
      )}
    </section>
  );
};

export default ClientSupplementsPanel;
